// GuildWeeklyRelease.tsx
// ---------------------------------------------------------------------------
// The per-week "release" switch, lifted out of GuildsPage so every place an
// officer might think about it can show the same control: the guild page
// header, the home page guild bar, and the raid header in the Planner.
//
// What release actually does (see raids.js candidate query): for one reset
// week, it makes members whose membership has allow_cross_guild = true visible
// as candidates in OTHER guilds' raid rosters. No release, no visibility, no
// matter who opted in. It is per week and it is per guild, so it never leaks
// beyond the week an officer deliberately flipped.
//
// That is easy to misread as "give my players away", which is why the control
// now carries its own explainer rather than relying on a hover tooltip.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatWeekRange } from "./lib/regionReset";
import { DEFAULT_RESET_RULE, ResetRule, getResetWeekForDate } from "./lib/time";
import { Floating } from "./Floating";

export type ReleaseContext = "guild" | "home" | "planner";

const WHERE: { key: ReleaseContext; text: string }[] = [
  { key: "guild", text: "On your guild page header" },
  { key: "home", text: "On the home page, next to your guild" },
  { key: "planner", text: "In the Planner, under Publish when you build a raid" },
];

export function GuildWeeklyRelease({
  guildId,
  highlightAt,
  context = "guild",
  className = "",
}: {
  guildId: string;
  /**
   * An instant (e.g. a raid's first session start) whose reset week is marked
   * as the one in context. Resolved with the guild's own rule, so a US raid on
   * a Tuesday marks the right week.
   */
  highlightAt?: string | null;
  /** Which of the three placements this instance is, for the "where to find it" list. */
  context?: ReleaseContext;
  className?: string;
}) {
  const [releaseWeeks, setReleaseWeeks] = useState<Record<string, boolean>>({});
  const [releaseLabels, setReleaseLabels] = useState<{ currentWeek: string; nextWeek: string }>({ currentWeek: '', nextWeek: '' });
  const [releaseLoaded, setReleaseLoaded] = useState(false);
  // This guild's full reset rule (Wednesday 03:00 UTC for EU, Tuesday 08:00
  // Pacific for US). From GET /api/guilds/:id/release-status.
  const [resetRule, setResetRule] = useState<ResetRule>(DEFAULT_RESET_RULE);
  const [infoOpen, setInfoOpen] = useState(false);
  const highlightDate = highlightAt ? new Date(highlightAt) : null;
  const highlightWeek =
    highlightDate && !Number.isNaN(highlightDate.getTime()) ? getResetWeekForDate(highlightDate, resetRule) : null;
  const [hoverWeek, setHoverWeek] = useState<string | null>(null);
  const infoBtnRef = useRef<HTMLButtonElement | null>(null);
  const infoPanelRef = useRef<HTMLDivElement | null>(null);
  const weekRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    fetch(`/api/guilds/${guildId}/release-status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setReleaseLabels({ currentWeek: data.currentWeek, nextWeek: data.nextWeek });
          if (data.resetRule) setResetRule(data.resetRule);
          setReleaseWeeks({
            [data.currentWeek]: !!data.weeks[data.currentWeek],
            [data.nextWeek]: !!data.weeks[data.nextWeek],
          });
          setReleaseLoaded(true);
        }
      }).catch(console.error);
  }, [guildId]);

  useEffect(() => {
    if (!infoOpen) return;
    const onDown = (e: MouseEvent) => {
      // The panel lives in a portal, so it is not inside the button's subtree:
      // both have to be checked before treating a click as "outside".
      const t = e.target as Node;
      if (infoBtnRef.current?.contains(t)) return;
      if (infoPanelRef.current?.contains(t)) return;
      setInfoOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setInfoOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen]);

  const toggleWeek = async (weekStart: string) => {
    const newVal = !releaseWeeks[weekStart];

    // Optimistic update — flip immediately so the toggle gives instant
    // feedback on click. Reverted below if the request fails. Without
    // this, slow networks make the toggle feel unresponsive and users
    // double-click thinking nothing happened.
    setReleaseWeeks(prev => ({ ...prev, [weekStart]: newVal }));

    try {
      const res = await fetch(`/api/guilds/${guildId}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ weekStart, released: newVal }),
      });

      if (!res.ok) {
        // Server rejected — revert the optimistic flip and surface the
        // error so the underlying problem is visible in the console.
        setReleaseWeeks(prev => ({ ...prev, [weekStart]: !newVal }));
        const errBody = await res.text().catch(() => '');
        console.error('[GuildWeeklyRelease] Toggle failed:', res.status, errBody);
      } else {
        // The guild page's Raid Ready bar counts released weeks, so tell it to
        // recount instead of waiting for a reload.
        window.dispatchEvent(new CustomEvent('lfm:guild-release-changed', { detail: { guildId, weekStart, released: newVal } }));
      }
    } catch (err) {
      // Network failure — revert the optimistic flip.
      setReleaseWeeks(prev => ({ ...prev, [weekStart]: !newVal }));
      console.error('[GuildWeeklyRelease] Toggle network error:', err);
    }
  };

  // `ws` is a week_start label (a Wednesday in every region), so it is resolved
  // to the guild's real reset day before display — otherwise a US guild's
  // officers see a span starting the day after it actually did.
  const formatWeekLabel = (ws: string) =>
    formatWeekRange(ws, resetRule).replace(' - ', ' – ');

  // Skeleton placeholder while loading — prevents layout shift when this
  // component sits on the same line as other inline controls.
  if (!releaseLoaded) {
    return (
      <div className={`flex shrink-0 items-center gap-2.5 opacity-30 ${className}`}>
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Release</span>
        <div className="w-9 h-5 rounded-full bg-[var(--c-sunken-2)]" />
        <div className="w-9 h-5 rounded-full bg-[var(--c-sunken-2)]" />
      </div>
    );
  }

  return (
    <div data-tour="guild-release" className={`flex shrink-0 items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--c-ink-3)] whitespace-nowrap">Release</span>
        <button
          ref={infoBtnRef}
          type="button"
          onClick={() => setInfoOpen(v => !v)}
          aria-label="What does release do?"
          aria-expanded={infoOpen}
          className={`grid h-4 w-4 place-items-center rounded-full border text-[9px] font-bold transition-colors ${
            infoOpen
              ? 'border-cyan-400/60 bg-cyan-500/20 text-cyan-800 dark:text-cyan-200'
              : 'border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:border-cyan-400/40 hover:text-cyan-700 dark:hover:text-cyan-300'
          }`}
        >
          ?
        </button>

        <Floating anchor={infoBtnRef.current} open={infoOpen}>
          <div
            ref={infoPanelRef}
            className="w-[320px] rounded-xl border border-cyan-400/25 bg-[var(--c-surface)] p-4 text-left shadow-2xl shadow-black/70"
          >
            <div className="flex items-center gap-2">
              <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-cyan-700 dark:text-cyan-300">
                Important
              </span>
              <span className="text-[12px] font-bold text-[var(--c-ink)]">What release does</span>
            </div>
            <p className="mt-2 text-[12px] leading-relaxed text-[var(--c-ink-2)]">
              Flipping a week on lends your raiders to the rest of the community for that week. Players who
              opted into cross-guild raiding become visible as candidates in other guilds' raids, so your
              bench still gets to play and other guilds can fill their gaps.
            </p>
            <p className="mt-2 text-[12px] leading-relaxed text-[var(--c-ink-2)]">
              Nobody is lent out unless they opted in themselves, and it only ever covers the single week you
              toggle. Leave a week off and your roster stays invisible to everyone else.
            </p>
            <div className="mt-3 border-t border-[var(--c-line)] pt-2.5">
              <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--c-ink-4)]">Where to find it</div>
              <ul className="mt-1.5 space-y-1 text-[11.5px]">
                {WHERE.map(w => (
                  <li key={w.key} className={w.key === context ? "text-[var(--c-ink-2)]" : "text-[var(--c-ink-3)]"}>
                    · {w.key === context && <span className="font-bold text-cyan-700 dark:text-cyan-300">Here. </span>}
                    {w.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Floating>
      </div>

      {[releaseLabels.currentWeek, releaseLabels.nextWeek].map(ws => {
        const active = releaseWeeks[ws];
        const isCurrent = ws === releaseLabels.currentWeek;
        const weekLabel = isCurrent ? 'This week' : 'Next week';
        const inContext = !!highlightWeek && highlightWeek === ws;
        return (
          <div
            key={ws}
            className="relative"
            onMouseEnter={() => setHoverWeek(ws)}
            onMouseLeave={() => setHoverWeek(prev => (prev === ws ? null : prev))}
          >
            <button
              ref={(el) => { weekRefs.current[ws] = el; }}
              onClick={() => toggleWeek(ws)}
              onFocus={() => setHoverWeek(ws)}
              onBlur={() => setHoverWeek(prev => (prev === ws ? null : prev))}
              role="switch"
              aria-checked={active}
              aria-label={`${active ? 'Revoke' : 'Activate'} release for ${weekLabel}`}
              className={`flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-[var(--c-sunken)] transition-colors ${
                inContext ? 'ring-1 ring-inset ring-cyan-400/30' : ''
              }`}
            >
              <span className={`text-[10px] font-medium whitespace-nowrap transition-colors ${active ? 'text-cyan-800 dark:text-cyan-200' : 'text-[var(--c-ink-3)]'}`}>
                {weekLabel}
              </span>
              {/* iOS-style toggle: track turns cyan when on, knob slides right */}
              <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${active ? 'bg-cyan-500' : 'bg-[var(--c-sunken-2)] ring-1 ring-inset ring-[var(--c-line-strong)]'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-all ${active ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
            </button>
            <Floating
              anchor={weekRefs.current[ws] || null}
              open={hoverWeek === ws}
              align="center"
              prefer="top"
            >
              <div className="pointer-events-none max-w-[300px] rounded-lg border border-[var(--c-line-strong)] bg-[var(--c-surface)] px-3 py-1.5 text-xs text-[var(--c-ink)] shadow-2xl">
                {active
                  ? <span>Click to <span className="text-red-600 dark:text-red-400 font-bold">revoke</span> release for <span className="text-cyan-700 dark:text-cyan-400 font-bold">{formatWeekLabel(ws)}</span></span>
                  : <span>Release opted-in players to other raids for <span className="text-cyan-700 dark:text-cyan-400 font-bold">{formatWeekLabel(ws)}</span></span>
                }
                {inContext && <span className="block text-[10px] text-cyan-700/70 dark:text-cyan-300/70">This is your raid's week</span>}
              </div>
            </Floating>
          </div>
        );
      })}
    </div>
  );
}

export default GuildWeeklyRelease;
